import React, { useState, useEffect, useRef } from 'react';
import { Download, Maximize2, Minimize2 } from 'lucide-react';
import { buscar, urlDescargar, getServerUrl, descargarEnNavegador } from '../utils/api';
import PdfViewer from '../components/PdfViewer';

function formatBytes(b) {
  if (!b) return '—';
  if (b < 1048576) return (b / 1024).toFixed(0) + ' KB';
  return (b / 1048576).toFixed(1) + ' MB';
}
function formatDate(s) {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('es-BO', { day:'2-digit', month:'2-digit', year:'numeric' });
}

export default function SearchPage({ gestiones, user }) {
  const [q, setQ] = useState('');
  const [tipo, setTipo] = useState('');
  const [gestion, setGestion] = useState('');
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [pantallaCompleta, setPantallaCompleta] = useState(false);
  const debounceRef = useRef(null);

  // Búsqueda automática al cambiar filtros
  useEffect(() => {
    if (!q && !tipo && !gestion) { setResults([]); setSearched(false); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => handleSearch(), 400);
    return () => clearTimeout(debounceRef.current);
  }, [q, tipo, gestion]);

  const handleSearch = async () => {
    setLoading(true);
    try {
      const data = await buscar({ q: q || undefined, tipo: tipo || undefined, gestion: gestion || undefined });
      setResults(data);
      setSearched(true);
    } catch {}
    setLoading(false);
  };

  const handleDescargar = async (doc) => {
    const url = urlDescargar(doc.id);
    if (window.electronAPI) window.electronAPI.openExternal(url);
    else {
      try { await descargarEnNavegador(url, doc.nombre_original); }
      catch { window.open(url, '_blank'); }
    }
  };

  const getViewUrl = (doc) => {
    const token = localStorage.getItem('token');
    return `${getServerUrl()}/api/documentos/${doc.id}/ver?token=${token}`;
  };

  const limpiar = () => { setQ(''); setTipo(''); setGestion(''); setResults([]); setSearched(false); setViewing(null); };

  return (
    <div className={`content-split ${viewing ? 'has-active' : ''}`}>
      <div className="search-page" style={{ flex: viewing ? '0 0 360px' : 1, minWidth: 280 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600 }}>Búsqueda de documentos</h2>
          {(q || tipo || gestion) && (
            <button className="btn-action btn-outline btn-sm" onClick={limpiar}>Limpiar filtros</button>
          )}
        </div>

        {/* Filtros */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          {/* Buscador de texto */}
          <div style={{ position: 'relative' }}>
            <svg style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', opacity:0.4 }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              placeholder="Buscar por nombre de archivo..."
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              style={{ width:'100%', padding:'9px 12px 9px 32px', border:'1.5px solid #e0e0e0', borderRadius:8, fontSize:13, outline:'none' }}
              autoFocus
            />
            {loading && <span style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', fontSize:11, color:'#888' }}>buscando...</span>}
          </div>

          {/* Filtros secundarios en fila */}
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: '#888', display:'block', marginBottom:3 }}>Tipo de sesión</label>
              <select
                value={tipo}
                onChange={e => setTipo(e.target.value)}
                style={{ width:'100%', padding:'7px 10px', border:'1.5px solid #e0e0e0', borderRadius:7, fontSize:12, outline:'none', background:'white' }}
              >
                <option value="">Todas</option>
                <option value="ordinaria">Ordinarias</option>
                <option value="extraordinaria">Extraordinarias</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: '#888', display:'block', marginBottom:3 }}>Gestión</label>
              <select
                value={gestion}
                onChange={e => setGestion(e.target.value)}
                style={{ width:'100%', padding:'7px 10px', border:'1.5px solid #e0e0e0', borderRadius:7, fontSize:12, outline:'none', background:'white' }}
              >
                <option value="">Todas</option>
                {gestiones.map(g => (
                  <option key={g.id} value={g.anio_inicio}>{g.anio_inicio}-{g.anio_fin}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Resultados */}
        {searched && (
          <div>
            <p style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
              {loading ? 'Buscando...' : `${results.length} resultado${results.length !== 1 ? 's' : ''} encontrado${results.length !== 1 ? 's' : ''}`}
              {tipo && <span style={{ marginLeft:6, background:'#eff6ff', color:'#1d4ed8', borderRadius:4, padding:'1px 6px', fontSize:11 }}>{tipo}</span>}
              {gestion && <span style={{ marginLeft:4, background:'#f0fdf4', color:'#166534', borderRadius:4, padding:'1px 6px', fontSize:11 }}>Gestión {gestion}</span>}
            </p>
            <div className="search-results">
              {results.map(doc => (
                <div
                  key={doc.id}
                  className="search-result-card"
                  onClick={() => setViewing(viewing?.id === doc.id ? null : doc)}
                  style={{
                    borderColor: viewing?.id === doc.id ? '#c8102e' : undefined,
                    background: viewing?.id === doc.id ? '#fff5f6' : undefined
                  }}
                >
                  <svg width="28" height="34" viewBox="0 0 24 24" fill="none" stroke="#c8102e" strokeWidth="1.5" style={{ flexShrink:0 }}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                  </svg>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:500, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                      {doc.nombre_original}
                    </div>
                    <div style={{ fontSize:11, color:'#888', marginTop:2, display:'flex', gap:6, flexWrap:'wrap' }}>
                      <span>Gestión {doc.gestion_anio}</span>
                      <span>·</span>
                      <span style={{ textTransform:'capitalize' }}>{doc.sesion_tipo} N°{String(doc.sesion_numero).padStart(2,'0')}</span>
                      {doc.sesion_fecha && <><span>·</span><span>{formatDate(doc.sesion_fecha)}</span></>}
                      <span>·</span>
                      <span>{formatBytes(doc.tamano)}</span>
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:4, flexShrink:0 }}>
                    <button className="file-btn" onClick={e => { e.stopPropagation(); setViewing(viewing?.id === doc.id ? null : doc); }}>
                      Ver
                    </button>
                    <button className="file-btn" onClick={e => { e.stopPropagation(); handleDescargar(doc); }}>
                      Descargar
                    </button>
                  </div>
                </div>
              ))}
              {results.length === 0 && !loading && (
                <div style={{ textAlign:'center', padding:'40px 20px', color:'#aaa' }}>
                  <div style={{ fontSize:36, marginBottom:10 }}>🔍</div>
                  <p style={{ fontSize:13 }}>No se encontraron documentos</p>
                  <p style={{ fontSize:11, marginTop:4 }}>Prueba con otros términos o cambia los filtros</p>
                </div>
              )}
            </div>
          </div>
        )}

        {!searched && (
          <div style={{ textAlign:'center', padding:'60px 20px', color:'#bbb' }}>
            <div style={{ fontSize:48, marginBottom:12 }}>🔍</div>
            <p style={{ fontSize:13 }}>Escribe algo para buscar o selecciona un filtro</p>
          </div>
        )}
      </div>

      {/* Visor PDF */}
      {viewing && (
        <div className={`viewer-panel ${pantallaCompleta ? 'pantalla-completa' : ''}`} style={{ flex:1 }}>
          <div className="viewer-toolbar">
            <span style={{ fontSize:12, color:'rgba(255,255,255,0.7)' }}>{viewing.nombre_original}</span>
            <div style={{ display:'flex', gap:6 }}>
              <button className="viewer-btn" title="Pantalla completa" onClick={() => setPantallaCompleta(v => !v)}>
                {pantallaCompleta ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
              </button>
              <button className="viewer-btn" title="Descargar" onClick={() => handleDescargar(viewing)}><Download size={15} /></button>
              <button className="viewer-btn" onClick={() => { setViewing(null); setPantallaCompleta(false); }}>✕</button>
            </div>
          </div>
          {window.electronAPI ? (
            <iframe
              key={viewing.id}
              src={getViewUrl(viewing)}
              style={{ flex:1, width:'100%', border:'none', display:'block' }}
              title={viewing.nombre_original}
            />
          ) : (
            <PdfViewer key={viewing.id} src={getViewUrl(viewing)} />
          )}
        </div>
      )}
    </div>
  );
}
